import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  BookOpen, Plus, Search, Edit2, Trash2, Eye, EyeOff, Star, StarOff,
  FileText, Folder, Tag, Link as LinkIcon, Upload, Download, X,
  Loader2, ChevronRight, ExternalLink, Paperclip, Clock, BarChart3,
  Package, Wrench, Shield, AlertTriangle, CheckCircle, Building, Hammer
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Category icons mapping
const categoryIcons = {
  Package: Package,
  Wrench: Wrench,
  Shield: Shield,
  Tool: Hammer,
  Hammer: Hammer,
  FileText: FileText,
  CheckCircle: CheckCircle,
  AlertTriangle: AlertTriangle,
  Building: Building,
  Folder: Folder
};

// Category colors
const categoryColors = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
};

const AdminKnowledgeBasePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState({ items: [], total: 0 });
  const [tags, setTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeTab, setActiveTab] = useState('articles');
  
  // Article dialog state
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    summary: '',
    content: '',
    category_id: '',
    subcategory: '',
    tags: [],
    keywords: [],
    links: [],
    is_published: true,
    is_featured: false,
    access_level: 'internal',
    product_codes: [],
    procedure_type: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [productCodeInput, setProductCodeInput] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Link dialog state
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkForm, setLinkForm] = useState({
    title: '',
    url: '',
    link_type: 'external',
    description: ''
  });
  
  // Category dialog state
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: 'Folder',
    color: 'slate'
  });

  useEffect(() => {
    const isAdmin = localStorage.getItem('adminLoggedIn');
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, categoriesRes, articlesRes, tagsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/kb/stats`),
        axios.get(`${BACKEND_URL}/api/kb/categories`),
        axios.get(`${BACKEND_URL}/api/kb/articles?limit=50`),
        axios.get(`${BACKEND_URL}/api/kb/tags`)
      ]);
      
      setStats(statsRes.data);
      setCategories(categoriesRes.data);
      setArticles(articlesRes.data);
      setTags(tagsRes.data);
      
      // Seed categories if none exist
      if (categoriesRes.data.length === 0) {
        await axios.post(`${BACKEND_URL}/api/kb/seed-categories`);
        const newCats = await axios.get(`${BACKEND_URL}/api/kb/categories`);
        setCategories(newCats.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load knowledge base data');
    } finally {
      setLoading(false);
    }
  };

  const fetchArticles = async (categoryId = '', query = '') => {
    try {
      let url = `${BACKEND_URL}/api/kb/articles?limit=50`;
      if (categoryId) url += `&category_id=${categoryId}`;
      
      if (query) {
        const searchRes = await axios.get(`${BACKEND_URL}/api/kb/search?q=${encodeURIComponent(query)}`);
        setArticles({ items: searchRes.data.items, total: searchRes.data.total });
      } else {
        const res = await axios.get(url);
        setArticles(res.data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchArticles(selectedCategory, searchQuery);
  };

  const handleCategoryFilter = (categoryId) => {
    setSelectedCategory(categoryId);
    fetchArticles(categoryId, searchQuery);
  };

  const openArticleDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        title: article.title || '',
        summary: article.summary || '',
        content: article.content || '',
        category_id: article.category_id || '',
        subcategory: article.subcategory || '',
        tags: article.tags || [],
        keywords: article.keywords || [],
        links: article.links || [],
        is_published: article.is_published ?? true,
        is_featured: article.is_featured ?? false,
        access_level: article.access_level || 'internal',
        product_codes: article.product_codes || [],
        procedure_type: article.procedure_type || ''
      });
    } else {
      setEditingArticle(null);
      setArticleForm({
        title: '',
        summary: '',
        content: '',
        category_id: categories[0]?.id || '',
        subcategory: '',
        tags: [],
        keywords: [],
        links: [],
        is_published: true,
        is_featured: false,
        access_level: 'internal',
        product_codes: [],
        procedure_type: ''
      });
    }
    setShowArticleDialog(true);
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!articleForm.category_id) {
      toast.error('Category is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingArticle) {
        await axios.put(`${BACKEND_URL}/api/kb/articles/${editingArticle.id}`, articleForm);
        toast.success('Article updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/kb/articles`, articleForm);
        toast.success('Article created');
      }
      setShowArticleDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save article');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/kb/articles/${articleId}`);
      toast.success('Article deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleTogglePublished = async (article) => {
    try {
      await axios.put(`${BACKEND_URL}/api/kb/articles/${article.id}`, {
        is_published: !article.is_published
      });
      toast.success(article.is_published ? 'Article unpublished' : 'Article published');
      fetchData();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const handleToggleFeatured = async (article) => {
    try {
      await axios.put(`${BACKEND_URL}/api/kb/articles/${article.id}`, {
        is_featured: !article.is_featured
      });
      toast.success(article.is_featured ? 'Removed from featured' : 'Added to featured');
      fetchData();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !articleForm.tags.includes(tagInput.trim())) {
      setArticleForm({ ...articleForm, tags: [...articleForm.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setArticleForm({ ...articleForm, tags: articleForm.tags.filter(t => t !== tag) });
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !articleForm.keywords.includes(keywordInput.trim())) {
      setArticleForm({ ...articleForm, keywords: [...articleForm.keywords, keywordInput.trim()] });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setArticleForm({ ...articleForm, keywords: articleForm.keywords.filter(k => k !== keyword) });
  };

  const addProductCode = () => {
    if (productCodeInput.trim() && !articleForm.product_codes.includes(productCodeInput.trim())) {
      setArticleForm({ ...articleForm, product_codes: [...articleForm.product_codes, productCodeInput.trim()] });
      setProductCodeInput('');
    }
  };

  const removeProductCode = (code) => {
    setArticleForm({ ...articleForm, product_codes: articleForm.product_codes.filter(c => c !== code) });
  };

  const addLink = () => {
    if (linkForm.title.trim() && linkForm.url.trim()) {
      setArticleForm({
        ...articleForm,
        links: [...articleForm.links, { ...linkForm, id: Date.now().toString() }]
      });
      setLinkForm({ title: '', url: '', link_type: 'external', description: '' });
      setShowLinkDialog(false);
    }
  };

  const removeLink = (linkId) => {
    setArticleForm({
      ...articleForm,
      links: articleForm.links.filter(l => l.id !== linkId)
    });
  };

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name || '',
        description: category.description || '',
        icon: category.icon || 'Folder',
        color: category.color || 'slate'
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        description: '',
        icon: 'Folder',
        color: 'slate'
      });
    }
    setShowCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      if (editingCategory) {
        await axios.put(`${BACKEND_URL}/api/kb/categories/${editingCategory.id}`, categoryForm);
        toast.success('Category updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/kb/categories`, categoryForm);
        toast.success('Category created');
      }
      setShowCategoryDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/kb/categories/${categoryId}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  if (loading) {
    return (
      <AdminSidebar pageTitle="Knowledge Base" pageIcon={BookOpen}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar pageTitle="Knowledge Base" pageIcon={BookOpen}>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.published_articles || 0}</p>
                <p className="text-sm text-slate-500">Articles</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Folder className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_categories || 0}</p>
                <p className="text-sm text-slate-500">Categories</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Star className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.featured_articles || 0}</p>
                <p className="text-sm text-slate-500">Featured</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_views || 0}</p>
                <p className="text-sm text-slate-500">Total Views</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Actions */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles, keywords, products..."
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="outline">Search</Button>
        </form>
        <div className="flex gap-2">
          <Button onClick={() => openArticleDialog()} className="bg-cyan-600 hover:bg-cyan-700">
            <Plus className="w-4 h-4 mr-2" />
            New Article
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="articles">
            <FileText className="w-4 h-4 mr-2" />
            Articles
          </TabsTrigger>
          <TabsTrigger value="categories">
            <Folder className="w-4 h-4 mr-2" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="tags">
            <Tag className="w-4 h-4 mr-2" />
            Tags
          </TabsTrigger>
        </TabsList>

        {/* Articles Tab */}
        <TabsContent value="articles" className="space-y-4">
          <div className="flex gap-6">
            {/* Category Sidebar */}
            <div className="w-64 shrink-0 hidden lg:block">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Filter by Category</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <button
                    onClick={() => handleCategoryFilter('')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                      selectedCategory === '' ? 'bg-cyan-100 text-cyan-700' : 'hover:bg-slate-100'
                    }`}
                  >
                    All Categories
                  </button>
                  {categories.map((cat) => {
                    const IconComponent = categoryIcons[cat.icon] || Folder;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryFilter(cat.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                          selectedCategory === cat.id ? 'bg-cyan-100 text-cyan-700' : 'hover:bg-slate-100'
                        }`}
                      >
                        <IconComponent className="w-4 h-4" />
                        <span className="flex-1 truncate">{cat.name}</span>
                        <Badge variant="outline" className="text-xs">{cat.article_count || 0}</Badge>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Articles List */}
            <div className="flex-1 space-y-3">
              {articles.items?.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No articles yet</h3>
                    <p className="text-slate-600 mb-4">Create your first knowledge base article</p>
                    <Button onClick={() => openArticleDialog()}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Article
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                articles.items?.map((article) => (
                  <Card key={article.id} className="hover:border-cyan-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {!article.is_published && (
                              <Badge variant="outline" className="text-amber-600 border-amber-300">Draft</Badge>
                            )}
                            {article.is_featured && (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Star className="w-3 h-3 mr-1" />Featured
                              </Badge>
                            )}
                            <Badge variant="outline">{article.category_name}</Badge>
                            {article.procedure_type && (
                              <Badge variant="outline" className="text-purple-600">{article.procedure_type}</Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-slate-900">{article.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2 mt-1">{article.summary}</p>
                          
                          {/* Tags */}
                          {article.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {article.tags.slice(0, 5).map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                                  {tag}
                                </span>
                              ))}
                              {article.tags.length > 5 && (
                                <span className="text-xs text-slate-500">+{article.tags.length - 5} more</span>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {article.view_count || 0} views
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(article.updated_at).toLocaleDateString()}
                            </span>
                            {article.attachments?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Paperclip className="w-3 h-3" />
                                {article.attachments.length} files
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleFeatured(article)}
                            title={article.is_featured ? 'Remove from featured' : 'Add to featured'}
                          >
                            {article.is_featured ? (
                              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                            ) : (
                              <StarOff className="w-4 h-4 text-slate-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePublished(article)}
                            title={article.is_published ? 'Unpublish' : 'Publish'}
                          >
                            {article.is_published ? (
                              <Eye className="w-4 h-4 text-green-600" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-slate-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openArticleDialog(article)}
                          >
                            <Edit2 className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteArticle(article.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => openCategoryDialog()} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => {
              const IconComponent = categoryIcons[cat.icon] || Folder;
              const colorClass = categoryColors[cat.color] || categoryColors.slate;
              return (
                <Card key={cat.id} className={`border ${colorClass.split(' ')[2]}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{cat.name}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2">{cat.description}</p>
                        <p className="text-xs text-slate-500 mt-2">{cat.article_count || 0} articles</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openCategoryDialog(cat)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(cat.id)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Popular Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <button
                    key={t.tag}
                    onClick={() => {
                      setSearchQuery(t.tag);
                      fetchArticles('', t.tag);
                      setActiveTab('articles');
                    }}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-sm flex items-center gap-2"
                  >
                    <Tag className="w-3 h-3" />
                    {t.tag}
                    <Badge variant="outline" className="text-xs">{t.count}</Badge>
                  </button>
                ))}
                {tags.length === 0 && (
                  <p className="text-slate-500">No tags yet. Add tags to your articles to see them here.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Article Dialog */}
      <Dialog open={showArticleDialog} onOpenChange={setShowArticleDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Edit Article' : 'New Article'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={articleForm.title}
                  onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                  placeholder="Article title"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={articleForm.category_id}
                  onValueChange={(v) => setArticleForm({ ...articleForm, category_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Summary</Label>
              <Textarea
                value={articleForm.summary}
                onChange={(e) => setArticleForm({ ...articleForm, summary: e.target.value })}
                placeholder="Brief summary of the article..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={articleForm.content}
                onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                placeholder="Full article content (Markdown supported)..."
                rows={12}
                className="font-mono text-sm"
              />
            </div>
            
            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {articleForm.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Keywords */}
            <div className="space-y-2">
              <Label>Search Keywords</Label>
              <div className="flex gap-2">
                <Input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  placeholder="Add keyword..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                />
                <Button type="button" variant="outline" onClick={addKeyword}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {articleForm.keywords.map((kw) => (
                  <Badge key={kw} variant="outline" className="gap-1">
                    {kw}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeKeyword(kw)} />
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-slate-500">Additional terms that should match in search but don't need to be displayed</p>
            </div>
            
            {/* Product Codes */}
            <div className="space-y-2">
              <Label>Product Codes / SKUs</Label>
              <div className="flex gap-2">
                <Input
                  value={productCodeInput}
                  onChange={(e) => setProductCodeInput(e.target.value)}
                  placeholder="Add product code..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addProductCode())}
                />
                <Button type="button" variant="outline" onClick={addProductCode}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {articleForm.product_codes.map((code) => (
                  <Badge key={code} className="bg-blue-100 text-blue-700 gap-1">
                    {code}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeProductCode(code)} />
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Links */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Links & References</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowLinkDialog(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Link
                </Button>
              </div>
              <div className="space-y-2">
                {articleForm.links.map((link) => (
                  <div key={link.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <LinkIcon className="w-4 h-4 text-slate-400" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{link.title}</p>
                      <p className="text-xs text-slate-500 truncate">{link.url}</p>
                    </div>
                    <Badge variant="outline">{link.link_type}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => removeLink(link.id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Additional Options */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Procedure Type</Label>
                <Select
                  value={articleForm.procedure_type}
                  onValueChange={(v) => setArticleForm({ ...articleForm, procedure_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    <SelectItem value="installation">Installation</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="troubleshooting">Troubleshooting</SelectItem>
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="specification">Specification</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Access Level</Label>
                <Select
                  value={articleForm.access_level}
                  onValueChange={(v) => setArticleForm({ ...articleForm, access_level: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal Only</SelectItem>
                    <SelectItem value="restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subcategory</Label>
                <Input
                  value={articleForm.subcategory}
                  onChange={(e) => setArticleForm({ ...articleForm, subcategory: e.target.value })}
                  placeholder="Optional subcategory"
                />
              </div>
            </div>
            
            {/* Checkboxes */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={articleForm.is_published}
                  onCheckedChange={(c) => setArticleForm({ ...articleForm, is_published: c })}
                />
                <span>Published</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={articleForm.is_featured}
                  onCheckedChange={(c) => setArticleForm({ ...articleForm, is_featured: c })}
                />
                <span>Featured</span>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSaveArticle} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingArticle ? 'Update Article' : 'Create Article'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Link Title</Label>
              <Input
                value={linkForm.title}
                onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                placeholder="Display title for the link"
              />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={linkForm.url}
                onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label>Link Type</Label>
              <Select
                value={linkForm.link_type}
                onValueChange={(v) => setLinkForm({ ...linkForm, link_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="external">External Link</SelectItem>
                  <SelectItem value="internal">Internal (Site Page)</SelectItem>
                  <SelectItem value="kb_article">KB Article</SelectItem>
                  <SelectItem value="document">Document/PDF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={linkForm.description}
                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={addLink}>Add Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Category name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={categoryForm.icon}
                  onValueChange={(v) => setCategoryForm({ ...categoryForm, icon: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Folder">Folder</SelectItem>
                    <SelectItem value="Package">Package</SelectItem>
                    <SelectItem value="Wrench">Wrench</SelectItem>
                    <SelectItem value="Shield">Shield</SelectItem>
                    <SelectItem value="Tool">Tool</SelectItem>
                    <SelectItem value="FileText">Document</SelectItem>
                    <SelectItem value="CheckCircle">Checkmark</SelectItem>
                    <SelectItem value="AlertTriangle">Warning</SelectItem>
                    <SelectItem value="Building">Building</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={categoryForm.color}
                  onValueChange={(v) => setCategoryForm({ ...categoryForm, color: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="slate">Gray</SelectItem>
                    <SelectItem value="blue">Blue</SelectItem>
                    <SelectItem value="green">Green</SelectItem>
                    <SelectItem value="red">Red</SelectItem>
                    <SelectItem value="amber">Amber</SelectItem>
                    <SelectItem value="purple">Purple</SelectItem>
                    <SelectItem value="cyan">Cyan</SelectItem>
                    <SelectItem value="orange">Orange</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSaveCategory}>
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSidebar>
  );
};

export default AdminKnowledgeBasePage;
